import { useState } from "react";
import { Lock, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { SectionHeader, FadeIn } from "./primitives";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { toast } from "@/components/ui/sonner";
import { DASHBOARD } from "@/constants/testIds";

export const JournalSection = () => {
  const [entries, setEntries] = useLocalStorage("ts.journal", []);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");

  const add = () => {
    const text = draft.trim();
    if (!text) return;
    const entry = {
      id: Date.now(),
      text,
      date: new Date().toLocaleDateString(undefined, {
        weekday: "long",
        day: "numeric",
        month: "long",
      }),
    };
    setEntries((prev) => [entry, ...prev]);
    setDraft("");
    setOpen(false);
    toast.success("Saved to your journal", { description: "Private to you." });
  };

  const remove = (id) => setEntries((prev) => prev.filter((e) => e.id !== id));

  return (
    <FadeIn>
      <section>
        <SectionHeader
          title="Journal"
          caption={
            <span className="flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5" /> Private to you
            </span>
          }
        />

        {entries.length > 0 ? (
          <div className="mb-4 space-y-3">
            {entries.map((e) => (
              <Card key={e.id} className="group flex items-start justify-between gap-4 p-4 shadow-soft">
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{e.date}</p>
                  <p className="mt-1 font-display text-[15px] leading-relaxed text-foreground/90">{e.text}</p>
                </div>
                <button
                  type="button"
                  onClick={() => remove(e.id)}
                  aria-label="Delete entry"
                  className="shrink-0 rounded-md p-1.5 text-muted-foreground opacity-0 transition-all hover:bg-secondary hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </Card>
            ))}
          </div>
        ) : null}

        <Card className="flex flex-col items-center justify-center border border-dashed border-border-strong/70 bg-card/40 p-10 text-center shadow-none">
          <h3 className="font-display text-xl text-foreground">When you’re ready, write a line.</h3>
          <p className="mt-1.5 text-[14px] text-muted-foreground">Nobody else will read it. Not even us.</p>
          <Button className="mt-5" onClick={() => setOpen(true)} data-testid={DASHBOARD.journalWrite}>
            Write a line
          </Button>
        </Card>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-xl font-medium">A line for today</DialogTitle>
              <DialogDescription>Honest and short is perfect. Private to you.</DialogDescription>
            </DialogHeader>
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={4}
              placeholder="Today, I…"
              data-testid={DASHBOARD.journalInput}
              className="resize-none border-border bg-card-muted"
            />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={add} data-testid={DASHBOARD.journalSave}>Save line</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </section>
    </FadeIn>
  );
};
